import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { BoundingBox, Listing } from "@/types";
import { BboxStatusIcon } from "@/components/listings/BboxStatusIcon";

interface SiblingsPhotoMapProps {
  imageUrl: string;
  activeListing?: {
    id: string;
    displayTitle: string;
    price: number | null;
    condition: string;
    status: string;
    boundingBox: BoundingBox;
    thumbnailUrl: string | null;
  };
  siblings: Listing[];
}

const SIBLING_COLORS = ["#3b82f6", "#10b981", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"];
const ACTIVE_COLOR = "#0f766e";

function statusColor(status: string, baseColor: string): string {
  if (status === "cancelled") return "#9ca3af";
  if (status === "sold") return "#ef4444";
  if (status === "reserved") return "#f59e0b";
  return baseColor;
}

export function SiblingsPhotoMap({
  imageUrl,
  activeListing,
  siblings,
}: SiblingsPhotoMapProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const clearHoverTimeout = useRef<number | null>(null);

  const requestHover = useCallback((id: string | null) => {
    if (clearHoverTimeout.current != null) {
      window.clearTimeout(clearHoverTimeout.current);
      clearHoverTimeout.current = null;
    }
    if (id == null) {
      clearHoverTimeout.current = window.setTimeout(() => {
        setHoveredId(null);
        clearHoverTimeout.current = null;
      }, 100);
    } else {
      setHoveredId(id);
    }
  }, []);

  useEffect(() => () => {
    if (clearHoverTimeout.current != null) {
      window.clearTimeout(clearHoverTimeout.current);
    }
  }, []);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth > 0) {
        setAspectRatio(img.naturalHeight / img.naturalWidth);
      }
    };
    img.src = imageUrl;
  }, [imageUrl]);

  const siblingsWithBbox = siblings.filter((s) => s.boundingBox);

  const svgRef = useRef<SVGSVGElement>(null);
  const [svgWidth, setSvgWidth] = useState(0);
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setSvgWidth(entry.contentRect.width);
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const firstSiblingBox = siblingsWithBbox[0]?.boundingBox;
  const defaultBox: BoundingBox | null = activeListing
    ? activeListing.boundingBox
    : firstSiblingBox ?? null;
  const focusedBox: BoundingBox | null = (() => {
    if (activeListing && hoveredId === activeListing.id) return activeListing.boundingBox;
    if (hoveredId) {
      const sib = siblingsWithBbox.find((s) => s.id === hoveredId);
      if (sib?.boundingBox) return sib.boundingBox;
    }
    return defaultBox;
  })();

  const isHovering = hoveredId != null;
  // Target ~4 display px blur, expressed in viewBox units (viewBox is 1000 wide).
  const blurVb = svgWidth > 0 ? (4 * 1000) / svgWidth : 6;

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,18rem)]">
      <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
        <img src={imageUrl} alt="" className="block w-full" />
        <svg
          ref={svgRef}
          viewBox="0 0 1000 1000"
          className="absolute inset-0 h-full w-full"
          preserveAspectRatio="none"
        >
          <defs>
            <filter id="siblings-map-filter" x="0%" y="0%" width="100%" height="100%">
              <feColorMatrix type="saturate" values="0.2" />
              <feGaussianBlur stdDeviation={blurVb} edgeMode="duplicate" />
            </filter>
            {focusedBox && (
              <mask id="siblings-map-cutout">
                <rect x="0" y="0" width="1000" height="1000" fill="white" />
                <rect
                  x={focusedBox.xMin}
                  y={focusedBox.yMin}
                  width={focusedBox.xMax - focusedBox.xMin}
                  height={focusedBox.yMax - focusedBox.yMin}
                  fill="black"
                  rx={8}
                  style={{ transition: "x 180ms ease, y 180ms ease, width 180ms ease, height 180ms ease" }}
                />
              </mask>
            )}
          </defs>

          {/* Filtered overlay — fades in on hover, cuts out the focused bbox */}
          {focusedBox && (
            <g style={{ opacity: isHovering ? 1 : 0, transition: "opacity 200ms ease" }}>
              <image
                href={imageUrl}
                x={0}
                y={0}
                width={1000}
                height={1000}
                preserveAspectRatio="none"
                filter="url(#siblings-map-filter)"
                mask="url(#siblings-map-cutout)"
              />
              <rect
                x={0}
                y={0}
                width={1000}
                height={1000}
                fill="white"
                fillOpacity={0.3}
                mask="url(#siblings-map-cutout)"
              />
            </g>
          )}

          {activeListing && (
            <BboxShape
              box={activeListing.boundingBox}
              color={ACTIVE_COLOR}
              label={t("listings.current_listing")}
              price={activeListing.price}
              status={activeListing.status}
              isHovered={hoveredId === activeListing.id}
              isActive
              onMouseEnter={() => requestHover(activeListing.id)}
              onMouseLeave={() => requestHover(null)}
              aspectRatio={aspectRatio ?? 1}
            />
          )}
          {siblingsWithBbox.map((sib, i) => {
            const base = SIBLING_COLORS[i % SIBLING_COLORS.length];
            const color = statusColor(sib.status, base);
            const isClickable = sib.status !== "sold" && sib.status !== "cancelled";
            return (
              <BboxShape
                key={sib.id}
                box={sib.boundingBox!}
                color={color}
                label={sib.displayTitle}
                price={sib.price}
                status={sib.status}
                isHovered={hoveredId === sib.id}
                onClick={
                  isClickable
                    ? () => {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                        navigate(`/listings/${sib.id}`);
                      }
                    : undefined
                }
                onMouseEnter={() => requestHover(sib.id)}
                onMouseLeave={() => requestHover(null)}
                aspectRatio={aspectRatio ?? 1}
              />
            );
          })}
        </svg>
      </div>

      <div className="flex flex-col gap-2">
        {activeListing && (
          <SiblingListRow
            id={activeListing.id}
            title={activeListing.displayTitle}
            price={activeListing.price}
            condition={activeListing.condition}
            status={activeListing.status}
            thumbnailUrl={activeListing.thumbnailUrl}
            cropFallback={{ imageUrl, box: activeListing.boundingBox }}
            color={ACTIVE_COLOR}
            isActive
            isHovered={hoveredId === activeListing.id}
            onMouseEnter={() => requestHover(activeListing.id)}
            onMouseLeave={() => requestHover(null)}
          />
        )}
        {siblingsWithBbox.map((sib, i) => {
          const base = SIBLING_COLORS[i % SIBLING_COLORS.length];
          const color = statusColor(sib.status, base);
          return (
            <SiblingListRow
              key={sib.id}
              id={sib.id}
              title={sib.displayTitle}
              price={sib.price}
              condition={sib.condition}
              status={sib.status}
              thumbnailUrl={sib.game?.thumbnailUrl ?? null}
              cropFallback={{ imageUrl, box: sib.boundingBox! }}
              color={color}
              isHovered={hoveredId === sib.id}
              onMouseEnter={() => requestHover(sib.id)}
              onMouseLeave={() => requestHover(null)}
              href={`/listings/${sib.id}`}
            />
          );
        })}
      </div>
    </div>
  );
}

function BboxShape({
  box,
  color,
  label,
  price,
  status,
  isHovered,
  isActive,
  onClick,
  onMouseEnter,
  onMouseLeave,
  aspectRatio,
}: {
  box: BoundingBox;
  color: string;
  label: string;
  price: number | null;
  status: string;
  isHovered: boolean;
  isActive?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  aspectRatio: number;
}) {
  const bw = box.xMax - box.xMin;
  const bh = box.yMax - box.yMin;
  const isSold = status === "sold";
  const isReserved = status === "reserved";
  const isCancelled = status === "cancelled";
  const isInactive = isSold || isCancelled;
  const pillY = box.yMin + 4;
  const pillText = label;
  const pillWidth = Math.min(bw - 8, pillText.length * 9 + 20);
  const showPrice = price != null && !isCancelled;
  const priceText = showPrice ? `${price!.toFixed(2)} €` : "";
  const priceFontSize = 22;
  const priceWidth = showPrice ? priceText.length * priceFontSize * 0.62 + 24 : 0;
  const priceTagHeight = 38;

  return (
    <g
      className={onClick ? "pointer-events-auto cursor-pointer" : "pointer-events-auto"}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <rect
        x={box.xMin}
        y={box.yMin}
        width={bw}
        height={bh}
        fill={color}
        fillOpacity={isHovered ? 0.18 : isActive ? 0.12 : 0.05}
        stroke={color}
        strokeWidth={isActive ? 5 : isHovered ? 5 : isCancelled ? 2 : 3}
        strokeDasharray={isCancelled ? "8 4" : "0"}
        rx={8}
        style={{ transition: "stroke-width 120ms ease, fill-opacity 120ms ease" }}
      />
      {(isInactive || isReserved) && (
        <BboxStatusIcon
          cx={box.xMin + bw / 2}
          cy={box.yMin + bh / 2}
          status={status}
          color={color}
          size={Math.min(bw, bh) * 0.4}
          aspectRatio={aspectRatio}
        />
      )}
      {(isHovered || isActive) && (
        <>
          <rect
            x={box.xMin + 4}
            y={pillY}
            width={pillWidth}
            height={28}
            fill={color}
            fillOpacity={0.95}
            rx={5}
            style={{ transition: "opacity 150ms ease" }}
          />
          <text
            x={box.xMin + 12}
            y={pillY + 19}
            fill="white"
            fontSize={13}
            fontWeight="600"
          >
            {pillText}
          </text>
        </>
      )}
      {showPrice && (
        <g>
          <rect
            x={box.xMax - priceWidth - 4}
            y={box.yMin + 4}
            width={priceWidth}
            height={priceTagHeight}
            fill="white"
            fillOpacity={0.95}
            rx={6}
          />
          <rect
            x={box.xMax - priceWidth - 4}
            y={box.yMin + 4}
            width={priceWidth}
            height={priceTagHeight}
            fill={color}
            fillOpacity={0.12}
            stroke={color}
            strokeWidth={2}
            rx={6}
          />
          <text
            x={box.xMax - priceWidth - 4 + 12}
            y={box.yMin + 4 + 26}
            fill={color}
            fontSize={priceFontSize}
            fontWeight="800"
          >
            {priceText}
          </text>
        </g>
      )}
    </g>
  );
}

interface SiblingListRowProps {
  id: string;
  title: string;
  price: number | null;
  condition: string;
  status: string;
  thumbnailUrl: string | null;
  cropFallback?: { imageUrl: string; box: BoundingBox };
  color: string;
  isActive?: boolean;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  href?: string;
}

function SiblingListRow({
  title,
  price,
  status,
  thumbnailUrl,
  cropFallback,
  color,
  isActive,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  href,
}: SiblingListRowProps) {
  const { t } = useTranslation();
  const isInactive = status === "sold" || status === "cancelled";
  const statusLabel =
    status === "sold" ? t("listings.status_sold")
    : status === "reserved" ? t("listings.status_reserved")
    : status === "cancelled" ? t("listings.status_cancelled")
    : null;

  const content = (
    <>
      <span
        className="absolute left-0 top-0 h-full w-1 rounded-l-lg transition-opacity"
        style={{ backgroundColor: color, opacity: isHovered || isActive ? 1 : 0.45 }}
      />
      <div className="flex min-w-0 flex-1 items-center gap-2.5 pl-1.5">
        <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-100">
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt="" className="h-full w-full object-cover" />
          ) : cropFallback ? (
            <CroppedThumbnail imageUrl={cropFallback.imageUrl} box={cropFallback.box} />
          ) : null}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {isActive && (
              <span className="inline-flex items-center rounded-full bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-700">
                {t("listings.current_listing_short")}
              </span>
            )}
            <p className="truncate text-sm font-medium text-gray-900">{title}</p>
          </div>
          {statusLabel && (
            <span className="mt-0.5 inline-block text-[10px] font-medium uppercase tracking-wide text-gray-500">
              {statusLabel}
            </span>
          )}
        </div>
        {price != null && (
          <span className={`shrink-0 text-sm font-bold tabular-nums ${isInactive ? "text-gray-400 line-through" : "text-primary-800"}`}>
            {price.toFixed(2)} €
          </span>
        )}
      </div>
    </>
  );

  const baseClass = `relative flex items-center rounded-lg border p-2 transition-colors ${
    isHovered
      ? "border-primary-300 bg-primary-50/50"
      : isActive
        ? "border-teal-200 bg-teal-50/40"
        : "border-gray-200 bg-white hover:border-primary-200"
  }`;

  if (href) {
    return (
      <Link
        to={href}
        className={baseClass}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        {content}
      </Link>
    );
  }

  return (
    <div
      className={baseClass}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {content}
    </div>
  );
}

function CroppedThumbnail({ imageUrl, box }: { imageUrl: string; box: BoundingBox }) {
  const bw = Math.max(1, box.xMax - box.xMin);
  const bh = Math.max(1, box.yMax - box.yMin);
  return (
    <img
      src={imageUrl}
      alt=""
      style={{
        position: "absolute",
        width: `${(1000 / bw) * 100}%`,
        height: `${(1000 / bh) * 100}%`,
        left: `${(-box.xMin / bw) * 100}%`,
        top: `${(-box.yMin / bh) * 100}%`,
        maxWidth: "none",
      }}
    />
  );
}
